/**
 * Hall of Honor - ランキング表示画面
 *
 * マップ別カラム定義で、Name+Score は左固定、残りは横スクロール。
 */

import { useState, useEffect } from 'react';
import { GAME_REGISTRY } from '../config/gameRegistry';
import { fetchRankings, type RankingResponse, type RankingEntry } from '../utils/scoreApi';
import { useI18n, interpolate } from '../i18n';
import type { TranslationKeys } from '../i18n/types';
import { BASE_PATH } from '../config';

const MAP_OPTIONS = Object.entries(GAME_REGISTRY)
  .filter(([_, entry]) => !entry.isWIP)
  .map(([id, entry]) => ({
    id,
    apiMapType: entry.apiMapType,
    displayName: entry.displayName,
  }));

type Period = 'all' | 'week' | 'month';

const PERIODS: Period[] = ['all', 'week', 'month'];

const PAGE_SIZE = 20;

// ---------- マップ別カラム定義 ----------

interface ColumnDef {
  /** i18n キー（hallOfHonor 内） */
  labelKey: keyof TranslationKeys['hallOfHonor'];
  /** metadata から値を取得する関数 */
  getValue: (entry: RankingEntry) => string | number;
}

/** Detroit 以外の共通: Score 列は finalScore */
const scoreColumn: ColumnDef = {
  labelKey: 'score',
  getValue: (e) => e.finalScore,
};

/** Detroit: Turn がメインスコア */
const turnColumn: ColumnDef = {
  labelKey: 'turn',
  getValue: (e) => e.turnCount,
};

const cashCol: ColumnDef = { labelKey: 'cash', getValue: (e) => e.metadata?.cash as number ?? '—' };
const sharesCol: ColumnDef = { labelKey: 'shares', getValue: (e) => e.metadata?.sharesIssued as number ?? '—' };
const locoCol: ColumnDef = { labelKey: 'locoLv', getValue: (e) => e.metadata?.locomotiveLevel as number ?? '—' };
const linksCol: ColumnDef = { labelKey: 'links', getValue: (e) => e.metadata?.completedLinks as number ?? '—' };
const undoCol: ColumnDef = { labelKey: 'undoCount', getValue: (e) => e.metadata?.undoCount as number ?? 0 };
const cargoCol: ColumnDef = { labelKey: 'cargoLv', getValue: (e) => e.metadata?.cargoTransportLevel as number ?? '—' };
const deliveredCol: ColumnDef = { labelKey: 'delivered', getValue: (e) => e.metadata?.deliveredCubes as number ?? '—' };
const remainingCol: ColumnDef = { labelKey: 'remaining', getValue: (e) => e.metadata?.remainingGoods as number ?? '—' };
const unconnectedCol: ColumnDef = { labelKey: 'unconnected', getValue: (e) => e.metadata?.unconnectedStations as number ?? '—' };

/** マップ別カラム設定: [primaryScore, ...detailColumns] */
function getColumnsForMap(mapType: string): { primary: ColumnDef; details: ColumnDef[] } {
  switch (mapType) {
    case 'detroit_bankruptcy_solo':
      return {
        primary: turnColumn,
        details: [sharesCol, locoCol, linksCol, undoCol],
      };
    case 'seattle_metro_solo':
      return {
        primary: scoreColumn,
        details: [cashCol, sharesCol, locoCol, linksCol, cargoCol, deliveredCol, remainingCol, unconnectedCol, undoCol],
      };
    default:
      // Barbados, Puerto Rico
      return {
        primary: scoreColumn,
        details: [cashCol, sharesCol, locoCol, linksCol, undoCol],
      };
  }
}

// ---------- Component ----------

interface HallOfHonorProps {
  initialMapType?: string;
  highlightRank?: number;
}

export function HallOfHonor({ initialMapType, highlightRank }: HallOfHonorProps) {
  const { t, language } = useI18n();
  const [selectedApiMapType, setSelectedApiMapType] = useState(
    initialMapType || MAP_OPTIONS[0]?.apiMapType || 'barbados',
  );
  const [period, setPeriod] = useState<Period>('all');
  const [noUndo, setNoUndo] = useState(false);
  const [page, setPage] = useState(0);
  const [data, setData] = useState<RankingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const periodLabels: Record<Period, string> = {
    all: t.hallOfHonor.periodAll,
    week: t.hallOfHonor.periodWeekly,
    month: t.hallOfHonor.periodMonthly,
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchRankings(selectedApiMapType, period, PAGE_SIZE, page * PAGE_SIZE, noUndo)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        setError(t.hallOfHonor.error);
        setLoading(false);
      });
  }, [selectedApiMapType, period, page, noUndo]);

  const handleMapChange = (apiMapType: string) => {
    setSelectedApiMapType(apiMapType);
    setPage(0);
    setNoUndo(false);
  };

  const handlePeriodChange = (newPeriod: Period) => {
    setPeriod(newPeriod);
    setPage(0);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const locale = language === 'ja' ? 'ja-JP' : 'en-US';
    return d.toLocaleDateString(locale, { year: '2-digit', month: 'numeric', day: 'numeric' });
  };

  const getRankLabel = (rank: number): string => {
    if (rank === 1) return t.hallOfHonor.rank1;
    if (rank === 2) return t.hallOfHonor.rank2;
    if (rank === 3) return t.hallOfHonor.rank3;
    return String(rank);
  };

  const { primary, details } = getColumnsForMap(selectedApiMapType);

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl md:text-2xl font-bold">{t.hallOfHonor.title}</h1>
          <button
            className="btn-secondary px-3 py-1.5 text-sm"
            onClick={() => { window.location.href = BASE_PATH; }}
          >
            {t.hallOfHonor.back}
          </button>
        </div>

        {/* Controls: Map + Period */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <select
            className="px-3 py-2 text-sm rounded border border-border bg-background text-foreground"
            value={selectedApiMapType}
            onChange={(e) => handleMapChange(e.target.value)}
          >
            {MAP_OPTIONS.map((m) => (
              <option key={m.id} value={m.apiMapType}>
                {m.displayName}
              </option>
            ))}
          </select>

          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                  period === p
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                onClick={() => handlePeriodChange(p)}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>

          {/* No-Undo toggle */}
          <div className="flex gap-1">
            {([false, true] as const).map((val) => (
              <button
                key={String(val)}
                className={`px-3 py-1.5 text-sm rounded font-medium transition-colors ${
                  noUndo === val
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                onClick={() => { setNoUndo(val); setPage(0); }}
              >
                {val ? t.hallOfHonor.filterNoUndo : t.hallOfHonor.filterAll}
              </button>
            ))}
          </div>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            {t.hallOfHonor.loading}
          </div>
        )}
        {error && (
          <div className="text-center py-12 text-destructive text-sm">
            {error}
          </div>
        )}

        {/* Rankings Table */}
        {!loading && !error && data && (
          <>
            {data.scores.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                {t.hallOfHonor.noScores}
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 px-4">
                <table className="w-max min-w-full text-sm border-collapse whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground text-xs">
                      <th className="py-2 px-2 w-10">{t.hallOfHonor.rank}</th>
                      <th className="py-2 px-2 min-w-[5rem]">{t.hallOfHonor.player}</th>
                      <th className="py-2 px-2 text-right font-semibold">
                        {t.hallOfHonor[primary.labelKey]}
                      </th>
                      {details.map((col) => (
                        <th key={col.labelKey} className="py-2 px-2 text-right">
                          {t.hallOfHonor[col.labelKey]}
                        </th>
                      ))}
                      <th className="py-2 px-2 text-right">{t.hallOfHonor.date}</th>
                      <th className="py-2 px-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.scores.map((entry) => {
                      const isHighlighted = highlightRank === entry.rank;
                      return (
                        <tr
                          key={`${entry.rank}-${entry.playerName}`}
                          className={`border-b border-border/50 ${
                            isHighlighted ? 'bg-primary/10 font-bold' : ''
                          } ${entry.rank <= 3 ? 'font-medium' : ''}`}
                        >
                          <td className="py-1.5 px-2">
                            {entry.rank <= 3 ? getRankLabel(entry.rank) : entry.rank}
                          </td>
                          <td className="py-1.5 px-2 max-w-[8rem] truncate">
                            {entry.playerName}
                            {isHighlighted && <span className="ml-1 text-primary">*</span>}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums font-semibold">
                            {primary.getValue(entry)}
                          </td>
                          {details.map((col) => (
                            <td key={col.labelKey} className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">
                              {col.getValue(entry)}
                            </td>
                          ))}
                          <td className="py-1.5 px-2 text-right text-muted-foreground text-xs">
                            {formatDate(entry.playedAt)}
                          </td>
                          <td className="py-1.5 px-2 text-right">
                            {entry.hasReplay && (
                              <button
                                className="text-xs text-primary hover:underline font-medium"
                                onClick={() => {
                                  window.location.href = `${BASE_PATH}replay?map=${selectedApiMapType}&rank=${entry.rank}${noUndo ? '&noUndo=true' : ''}`;
                                }}
                              >
                                {t.hallOfHonor.watch}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {data.total > PAGE_SIZE && (
              <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                <button
                  className="btn-secondary px-3 py-1.5 text-sm"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  {t.hallOfHonor.prev}
                </button>
                <span className="text-muted-foreground">
                  {page + 1} / {totalPages}
                </span>
                <button
                  className="btn-secondary px-3 py-1.5 text-sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  {t.hallOfHonor.next}
                </button>
              </div>
            )}

            {/* Total count */}
            <div className="text-center mt-3 text-xs text-muted-foreground">
              {interpolate(t.hallOfHonor.scoresRegistered, { count: data.total })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
